import React, { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import CtaBanner from "../components/CtaBanner";
import HtmlContent from "../components/HtmlContent";
import PageBanner from "../components/PageBanner";
import ServiceImagesGallery from "../components/ServiceImagesGallery";
import { useServiceById } from "../api/queries";
import { preloadPageBannerImageLink } from "../lib/banners";
import { extractBullets, stripServiceDescriptionLead, stripListsFromContent, looksLikeHtml } from "../lib/html";
import { type, typeRichDescription } from "../lib/typography";

function RelatedServiceCard({ service, index }) {
  const Icon = Icons[service.icon] || Icons.Hammer;

  return (
    <Link
      to={`/services/${service.id}`}
      className="group bg-white rounded-2xl border border-slate-200 hover:border-[#1e6fcf] hover:shadow-2xl hover:shadow-[#1e6fcf]/10 transition-all duration-500 overflow-hidden"
    >
      <div className="relative h-48 overflow-hidden bg-[#0a1a2f]">
        {service.image ? (
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0a1a2f] via-[#0f2540] to-[#1e6fcf]/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a2f]/85 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-[#1e6fcf] flex items-center justify-center shadow-lg">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="absolute top-4 right-4 text-xs font-mono text-white/80 tracking-widest px-2 py-1 rounded bg-black/25 backdrop-blur-sm">
          {String(service.position ?? index + 1).padStart(2, "0")}
        </div>
      </div>
      <div className="p-5">
        <h3 className={`${type.cardTitle} text-[#0a1a2f] group-hover:text-[#1e6fcf] transition-colors`}>
          {service.title}
        </h3>
        <div className="mt-3 flex items-center gap-1.5 text-[#1e6fcf] font-bold text-xs uppercase tracking-wider">
          View service
          <ArrowUpRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}

export default function ServiceDetailPage() {
  const { id } = useParams();
  const { service, isLoading, isFetching, error } = useServiceById(id);

  const bannerImage = service?.bannerImageUrl || null;

  useEffect(() => {
    if (bannerImage) preloadPageBannerImageLink(bannerImage);
  }, [bannerImage]);

  const bodyDescription = useMemo(() => {
    if (!service?.description) return "";
    const cleaned = stripServiceDescriptionLead(service.description, service.title);
    return stripListsFromContent(cleaned);
  }, [service]);

  const bullets = useMemo(() => {
    if (!service) return [];
    if (service.bulletPoints?.length > 0) return service.bulletPoints;
    const source = stripServiceDescriptionLead(service.description, service.title);
    return extractBullets(source).map((text, i) => ({ id: i, text }));
  }, [service]);

  const prevService = useMemo(() => {
    if (!service?.related?.length) return null;
    const all = [...service.related, service].sort((a, b) => (a.position || 0) - (b.position || 0));
    const idx = all.findIndex((s) => String(s.id) === String(service.id));
    return idx > 0 ? all[idx - 1] : null;
  }, [service]);

  const nextService = useMemo(() => {
    if (!service?.related?.length) return null;
    const all = [...service.related, service].sort((a, b) => (a.position || 0) - (b.position || 0));
    const idx = all.findIndex((s) => String(s.id) === String(service.id));
    return idx < all.length - 1 ? all[idx + 1] : null;
  }, [service]);

  if (!isLoading && !isFetching && !service && !error) {
    return <Navigate to="/services" replace />;
  }

  const related = service?.related || [];
  const galleryImages = service?.imageUrls || [];
  const descriptionIsHtml = looksLikeHtml(bodyDescription);
  const contentLoading = isLoading && !service;

  return (
    <>
      <PageBanner
        image={bannerImage}
        isLoading={contentLoading && !bannerImage}
      />

      {contentLoading ? (
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center text-slate-500">Loading service...</div>
        </section>
      ) : !service ? (
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center text-slate-500">
            {error?.message || "Service not found."}
          </div>
        </section>
      ) : (
        <>
      {(prevService || nextService) && (
        <div className="border-b border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between gap-4">
            {prevService ? (
              <Link
                to={`/services/${prevService.id}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#1e6fcf] transition-colors min-w-0"
              >
                <ChevronLeft className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{prevService.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {nextService ? (
              <Link
                to={`/services/${nextService.id}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#1e6fcf] transition-colors min-w-0 text-right"
              >
                <span className="truncate">{nextService.title}</span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
              </Link>
            ) : (
              <span />
            )}
          </div>
        </div>
      )}

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_300px] gap-14">
          <article>
            {galleryImages.length > 0 && (
              <div className="mb-10">
                <ServiceImagesGallery images={galleryImages} title={service.title} />
              </div>
            )}

            <div className="prose prose-slate max-w-none">
              <HtmlContent
                html={bodyDescription}
                className={`${typeRichDescription} ${
                  descriptionIsHtml ? "" : "whitespace-pre-line"
                }`}
              />
            </div>

            {bullets.length > 0 && (
              <div className="mt-12">
                <h2 className={`${type.label} text-[#1758a8] mb-5`}>
                  Key capabilities
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {bullets.map((point) => (
                    <div
                      key={point.id}
                      className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#1e6fcf]/30 hover:bg-[#e8f2fc]/40 transition-colors"
                    >
                      <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#1e6fcf]/15 flex items-center justify-center mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-[#1e6fcf]" />
                      </span>
                      <span className={`${type.bodySm} text-slate-700 font-medium`}>{point.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-[#0a1a2f] to-[#0f2747] text-white">
              <h3 className={`${type.cardTitle} text-white`}>Ready to discuss this service?</h3>
              <p className={`mt-2 ${type.bodySm} text-slate-300 max-w-xl`}>
                Our engineering team can assess your project requirements and recommend the right approach.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 bg-[#1e6fcf] hover:bg-[#1758a8] text-white font-bold px-6 py-3 rounded-lg transition-all text-sm uppercase tracking-wider"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <Link
              to="/services"
              className="mt-10 inline-flex items-center gap-2 text-[#1e6fcf] hover:text-[#1758a8] font-bold text-sm uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all services
            </Link>
          </article>

          {related.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-32">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-4">
                    Other services
                  </div>
                  <ul className="space-y-1.5">
                    {related.map((item) => {
                      const ItemIcon = Icons[item.icon] || Icons.Hammer;
                      const isActive = String(item.id) === String(service.id);
                      return (
                        <li key={item.id}>
                          <Link
                            to={`/services/${item.id}`}
                            className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                              isActive
                                ? "bg-[#1e6fcf] text-white shadow-md shadow-[#1e6fcf]/25"
                                : "text-[#0a1a2f] hover:bg-white hover:shadow-sm"
                            }`}
                          >
                            <ItemIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="leading-snug">{item.title}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </aside>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
              <div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-0.5 bg-[#1e6fcf]" />
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#1758a8]">
                    Explore More
                  </span>
                </div>
                <h2 className="mt-4 text-3xl md:text-4xl font-black text-[#0a1a2f] tracking-tight">
                  Related Services
                </h2>
              </div>
              <Link
                to="/services"
                className="hidden lg:inline-flex items-center gap-2 text-[#0a1a2f] hover:text-[#1e6fcf] font-bold text-sm uppercase tracking-wider transition-colors"
              >
                View All <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.slice(0, 3).map((item) => (
                <RelatedServiceCard key={item.id} service={item} index={(item.position ?? 1) - 1} />
              ))}
            </div>
          </div>
        </section>
      )}
        </>
      )}

      <CtaBanner />
    </>
  );
}
